import { useEffect } from 'react'

/**
 * How tall the software keyboard is: the part of the layout viewport the visual viewport
 * no longer covers.
 *
 * `offsetTop` is deliberately **not** subtracted, and that is the whole point of this
 * function. It is how far iOS has shifted the visual viewport down inside the layout
 * viewport, which it does to reveal a focused input — so it rises exactly when the
 * keyboard is up. Subtracting it made the measurement collapse toward zero at that
 * moment, and everything keyed off "is the keyboard open" flipped back off mid-typing:
 * the tab bar reappeared between the composer and the keys, `--mobile-chrome-bottom`
 * un-collapsed so the surface lost another 56px, and the document-scroll reset stopped
 * firing, leaving the composer stranded in the middle of the screen with the header
 * scrolled off the top. The term was cancelling out the state it was trying to measure.
 *
 * `innerHeight - viewportHeight` is the keyboard's height whether or not iOS has shifted
 * anything, which is what makes it usable as a signal. Exported so the arithmetic is
 * testable without a renderer.
 */
export const resolveKeyboardInset = ({
  innerHeight,
  viewportHeight,
}: {
  innerHeight: number
  viewportHeight: number
}): number => Math.max(0, innerHeight - viewportHeight)

/**
 * Above this many pixels of bottom inset, treat the keyboard as open.
 *
 * Not `> 0`: Safari's collapsing address bar and the pull-to-refresh rubber band both
 * produce a small transient inset, and hiding the tab bar on those would make it flicker
 * on every scroll. No software keyboard is under 150px tall on any phone we support.
 */
/**
 * How far iOS has pushed the visual viewport down inside the layout viewport to reveal a
 * focused input.
 *
 * This is the number `resolveKeyboardInset` deliberately refuses to subtract, kept apart
 * from it because the two answer different questions. There it would corrupt a *size*
 * ("how tall is the keyboard"); here it is the *position* the visible area has moved to,
 * and everything anchored to the layout viewport has to be moved by it to stay on screen.
 *
 * `position: fixed` on iOS resolves against the layout viewport, which does not move — so
 * the mobile header and any surface pinned below it travel straight off the top of the
 * screen at exactly this distance. Publishing it lets CSS translate them back.
 *
 * Clamped at zero: iOS reports small negative offsets during the rubber-band at the top of
 * a scroll, and translating by a negative value would lift the header off-screen the other
 * way.
 *
 * **Zero unless something is actually covering part of the viewport**, which the clamp
 * alone did not achieve. Revealing a focused input is not the only thing that moves the
 * visual viewport: an ordinary overscroll does too, in the positive direction the clamp
 * lets through. So a swipe on a scrollable page shifted the whole fixed chrome down by
 * however far iOS had rubber-banded, then snapped it back as the offset returned to zero —
 * the header and the page under it visibly bouncing against the direction of the swipe.
 *
 * The discriminator is the *shrink*, not the shift. iOS only moves the viewport to reveal
 * something when it has also taken space away for whatever is doing the covering; an
 * overscroll shifts a viewport that is still full height. So the space lost is what says
 * the translation is owed, and `offsetTop` alone never is.
 *
 * The space lost has to be measured **both ways**, because iOS has two regimes and each
 * blinds one of the measurements (the same split `resolveKeyboardOpen` documents):
 *
 * - `keyboardInset > 0` — the browser-tab regime, where the layout viewport holds still and
 *   `innerHeight - viewportHeight` is the covering. Deliberately the raw inset rather than
 *   the keyboard flag: the flag's threshold is meant to tell a keyboard from lesser
 *   furniture, and iOS's form accessory bar (the `^ v Done` strip, which appears with a
 *   hardware keyboard and no keyboard at all) sits under it. Gating on the flag alone left
 *   the offset at zero while the viewport really was shifted, and the conversation header
 *   was pushed off the top of the screen for as long as the composer had the caret.
 * - `isKeyboardOpen` — the installed-standalone regime, where iOS shrinks `innerHeight` in
 *   lockstep with the visual viewport, so the inset reads ~0 with a full keyboard on
 *   screen. Gating on the inset alone shipped exactly the stranding this function exists to
 *   prevent, one regime over: the tab bar hid (the flag, built on the shrink-from-tallest,
 *   fired) while the surface stayed untranslated, and the composer sat at the top of the
 *   screen with dead space down to the keys — measured on the simulator against the PWA.
 *
 * Overscroll passes neither: the viewport it shifts is still full height, so the inset is
 * zero and the flag is off, in both regimes. That is the bounce fix, kept.
 */
export const resolveViewportOffsetTop = ({
  offsetTop,
  keyboardInset,
  isKeyboardOpen,
}: {
  offsetTop: number
  keyboardInset: number
  isKeyboardOpen: boolean
}): number => (keyboardInset > 0 || isKeyboardOpen ? Math.max(0, offsetTop) : 0)

export const KEYBOARD_OPEN_THRESHOLD = 150

/**
 * Set on `<html>` for as long as the software keyboard is up.
 *
 * Published as an attribute rather than returned as state for the reason the whole hook is:
 * the consumers are stylesheets in modules that know nothing about this file (the bottom tab
 * bar hides on it, surfaces stop reserving room for it), and a timeline of a thousand rows
 * cannot re-render on every frame of a keyboard animation.
 */
export const KEYBOARD_OPEN_ATTRIBUTE = 'data-keyboard-open'

/**
 * Whether the software keyboard is up right now, for a caller that has to act rather than
 * style — reading the same attribute the stylesheets do, so there is one answer in the app.
 *
 * The composer asks before parking focus on its keeper across a remount: with no keyboard on
 * screen there is nothing to hold open, which is also the answer on every desktop, where this
 * attribute is never set.
 */
export const isSoftKeyboardOpen = (): boolean =>
  document.documentElement.hasAttribute(KEYBOARD_OPEN_ATTRIBUTE)

/**
 * Input types that raise no keyboard — a checkbox or a file picker takes focus without any
 * furniture coming up from the bottom of the screen.
 */
const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
])

/**
 * Whether focus is on something iOS raises furniture for: a text field, a contenteditable, or
 * a `<select>`, whose picker wheel covers the bottom of the screen the way a keyboard does.
 *
 * The half of keyboard detection that does not depend on trusting a number iOS reports. Every
 * height in this file can be moved by a pinch, and two attempts at telling a pinch from a
 * keyboard by arithmetic alone shipped a tab bar that stayed hidden after a zoom. A keyboard
 * is only ever up for a focused editable, and a pinch focuses nothing — so with this as an
 * AND-gate, no amount of zooming can hide the tab bar on its own.
 */
export const hasKeyboardRaisingFocus = (activeElement: Element | null): boolean => {
  if (activeElement instanceof HTMLTextAreaElement || activeElement instanceof HTMLSelectElement) {
    return !activeElement.disabled
  }

  if (activeElement instanceof HTMLInputElement) {
    return (
      !activeElement.disabled &&
      !activeElement.readOnly &&
      !NON_TEXT_INPUT_TYPES.has(activeElement.type)
    )
  }

  /* By attribute rather than `isContentEditable`: both walk up to the editable region the
     caret sits in, but jsdom does not implement the property, so the property version reads
     `undefined` and cannot be tested. */
  return (
    activeElement instanceof HTMLElement &&
    activeElement.closest('[contenteditable=""], [contenteditable="true"]') !== null
  )
}

/**
 * How far from 1 the visual viewport's scale may drift and still count as unzoomed. iOS
 * settles a pinch a rounding error either side of 1 rather than exactly on it.
 */
export const PINCH_ZOOM_TOLERANCE = 0.01

/**
 * Whether the page is pinch-zoomed, which is the one thing `innerHeight` cannot be asked.
 *
 * Every height in this file is measured against `window.innerHeight`, and on iOS Safari that
 * number follows the *visual* viewport — it moves with a pinch just as `visualViewport.height`
 * does. So a zoom looks, from the two heights alone, exactly like no zoom at all: the earlier
 * attempt at this clamped the baseline to `innerHeight` believing the layout viewport held
 * still, the clamp was a no-op on a real phone, and the tab bar still never came back.
 * `scale` is the only reading that says a pinch happened.
 */
export const isPinchZoomed = (scale: number): boolean => Math.abs(scale - 1) > PINCH_ZOOM_TOLERANCE

/**
 * The visual viewport's height with the zoom taken back out of it, in screen terms rather
 * than CSS pixels.
 *
 * Zoomed out, iOS reports more CSS pixels than the screen has — the same screen drawn
 * smaller — and zoomed in it reports fewer. Multiplying by the scale that did it gives the
 * screen back, so a keyboard is the only thing left that can move this number.
 */
export const resolveScaledViewportHeight = ({
  viewportHeight,
  scale,
}: {
  viewportHeight: number
  scale: number
}): number => viewportHeight * scale

/**
 * The baseline `resolveKeyboardOpen` measures its shrink against: the tallest *scaled*
 * viewport seen since the last rotation. Only ever raised — standalone iOS shrinks the web
 * view with the keyboard, so lowering the stored peak to match would erase the very height
 * the shrink is measured from.
 */
export const resolveMaxViewportHeight = ({
  maxViewportHeight,
  scaledViewportHeight,
}: {
  maxViewportHeight: number
  scaledViewportHeight: number
}): number => Math.max(maxViewportHeight, scaledViewportHeight)

/**
 * Whether the software keyboard is up, measured two ways because iOS has two regimes.
 *
 * In a browser tab the layout viewport is left alone and only the visual viewport shrinks,
 * so `innerHeight - viewportHeight` IS the keyboard. Installed as a standalone app, iOS
 * resizes the web view itself, so `innerHeight` shrinks in lockstep and that difference
 * stays ~0 no matter how much keyboard is on screen — detection built on it alone never
 * fires, and the bottom tab bar stays up between the composer and the keys.
 *
 * Comparing against the tallest viewport seen catches that second regime, because the
 * viewport shrinking is the one thing true in both. Both heights are scaled, so a pinch
 * moves neither; the inset is passed as zero while zoomed, since the two raw heights it is
 * made of cannot be told apart from a zoom (see `isPinchZoomed`).
 *
 * All of which is still arithmetic over numbers iOS chooses, so none of it runs without a
 * focused editable — see `hasKeyboardRaisingFocus`. A pinch cannot focus anything, which is
 * what makes "the bar vanished when I zoomed" unreachable rather than merely accounted for.
 */
export const resolveKeyboardOpen = ({
  hasKeyboardFocus,
  keyboardInset,
  scaledViewportHeight,
  maxViewportHeight,
}: {
  hasKeyboardFocus: boolean
  keyboardInset: number
  scaledViewportHeight: number
  maxViewportHeight: number
}): boolean => {
  if (!hasKeyboardFocus) {
    return false
  }

  const shrinkFromTallest = Math.max(0, maxViewportHeight - scaledViewportHeight)

  return Math.max(keyboardInset, shrinkFromTallest) > KEYBOARD_OPEN_THRESHOLD
}

/**
 * Publishes the *visual* viewport to CSS as `--viewport-height` and
 * `--keyboard-inset` (both declared with sane defaults on `:root` in global.scss).
 *
 * `dvh` is not enough. It tracks the browser's collapsing toolbars, not the software
 * keyboard: when iOS raises the keyboard the layout viewport is unchanged, so a
 * `100dvh` surface keeps its full height and whatever sits at its bottom edge — the
 * message composer, most visibly — ends up underneath the keys.
 *
 * Written as custom properties rather than returned as state, because the consumer is
 * a layout. Re-rendering a timeline of a thousand message rows on every frame of a
 * keyboard animation is not affordable, and it isn't necessary: React never needs to
 * see this number, CSS does.
 *
 * rAF-coalesced because iOS fires `resize` and `scroll` together, many times, over
 * the course of the animation.
 */
const useVisualViewport = (isEnabled = true): void => {
  useEffect(() => {
    const viewport = window.visualViewport
    if (!isEnabled || !viewport) {
      return
    }

    let frame = 0
    // The baseline the shrink is measured against — see resolveMaxViewportHeight. Reset on
    // rotation, where the real height legitimately changes and a stale maximum would read as
    // a permanently-open keyboard.
    let maxViewportHeight = 0

    const publish = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const root = document.documentElement
        // Zero while pinch-zoomed: the gap between the two raw heights is then the zoom, not
        // a keyboard, and there is no way to tell how much of it is which.
        const inset = isPinchZoomed(viewport.scale)
          ? 0
          : resolveKeyboardInset({
              innerHeight: window.innerHeight,
              viewportHeight: viewport.height,
            })
        const scaledViewportHeight = resolveScaledViewportHeight({
          viewportHeight: viewport.height,
          scale: viewport.scale,
        })
        maxViewportHeight = resolveMaxViewportHeight({ maxViewportHeight, scaledViewportHeight })
        // A boolean for the things that are on or off rather than sized: the bottom tab
        // bar hides while typing (every native app does this — it is 56px of navigation
        // nobody wants mid-message), and surfaces that reserve room for it stop doing so.
        //
        // Resolved before the offset below, which needs it as the standalone-regime half
        // of its gate — see resolveViewportOffsetTop.
        const isKeyboardOpen = resolveKeyboardOpen({
          hasKeyboardFocus: hasKeyboardRaisingFocus(document.activeElement),
          keyboardInset: inset,
          scaledViewportHeight,
          maxViewportHeight,
        })

        root.style.setProperty('--viewport-height', `${viewport.height}px`)
        root.style.setProperty('--keyboard-inset', `${inset}px`)
        root.style.setProperty(
          '--viewport-offset-top',
          `${resolveViewportOffsetTop({
            offsetTop: viewport.offsetTop,
            keyboardInset: inset,
            isKeyboardOpen,
          })}px`,
        )
        root.toggleAttribute(KEYBOARD_OPEN_ATTRIBUTE, isKeyboardOpen)

        // Deliberately no `window.scrollTo(0, 0)` here. iOS reveals a focused input by
        // scrolling the document, and scrolling it back fires another viewport event, which
        // iOS answers by scrolling to reveal the input again — a loop with no exit, seen as
        // the whole surface juddering up and down for as long as the keyboard is open. The
        // fix is to remove the thing being fought over rather than to fight faster: an
        // surface like Messages locks the document (global.scss), and puts back whatever iOS
        // still moves once focus leaves the editable (useDocumentScrollLock).
      })
    }

    const resetBaseline = () => {
      maxViewportHeight = 0
      publish()
    }

    publish()
    viewport.addEventListener('resize', publish)
    viewport.addEventListener('scroll', publish)
    window.addEventListener('orientationchange', resetBaseline)
    // Focus is half the answer now, and it moves without the viewport always resizing behind
    // it — the `Done` key blurs the composer, and a tap from one field to the next never
    // changes a height at all.
    document.addEventListener('focusin', publish)
    document.addEventListener('focusout', publish)

    return () => {
      cancelAnimationFrame(frame)
      viewport.removeEventListener('resize', publish)
      viewport.removeEventListener('scroll', publish)
      window.removeEventListener('orientationchange', resetBaseline)
      document.removeEventListener('focusin', publish)
      document.removeEventListener('focusout', publish)
      // Back to the `:root` dvh fallback. Without this a surface that outlives the
      // hook — or a resize past the mobile breakpoint — would freeze at the last
      // measured pixel height.
      document.documentElement.style.removeProperty('--viewport-height')
      document.documentElement.style.removeProperty('--keyboard-inset')
      document.documentElement.style.removeProperty('--viewport-offset-top')
      document.documentElement.removeAttribute(KEYBOARD_OPEN_ATTRIBUTE)
    }
  }, [isEnabled])
}

export default useVisualViewport
